import React from 'react';
import styled from 'styled-components';

import SectionContainer from '../components/SectionContainer';
import PageHeader from '../components/PageHeader';
import Button from '../components/Button';

// Services Hero
const ServicesHero = styled.div`
  padding-top: 150px;
  padding-bottom: var(--spacing-lg);
  text-align: center;
`;

// Service Section
const ServiceSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  
  &:nth-child(even) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr !important;
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ServiceIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: var(--color-light-gray);
  border-radius: 50%;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const ServiceTitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const ServiceDescription = styled.div`
  margin-bottom: var(--spacing-md);
`;

const ServiceDetail = styled.p`
  margin-bottom: var(--spacing-sm);
  line-height: 1.8;
`;

const ServiceImage = styled.div<{ $bgImage: string }>`
  height: 400px;
  background-image: url(${props => props.$bgImage});
  background-size: cover;
  background-position: center;
  filter: grayscale(100%);
  transition: filter 0.3s ease;
  
  &:hover {
    filter: grayscale(0%);
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

// Process Section
const ProcessContainer = styled.div`
  margin-top: var(--spacing-lg);
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-xl);
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const ProcessRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  align-items: start; /* aligner le haut du paragraphe avec le haut de la photo */
  margin-bottom: var(--spacing-xl);

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }
`;

const ProcessGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  max-width: 800px;
  width: 100%;
  margin: 0; /* éviter le centrage dans la cellule de grille */
  justify-self: start; /* forcer l'ancrage à gauche */
`;

const ProcessStep = styled.div`
  text-align: left;
  position: relative;
  padding: var(--spacing-md);
  background-color: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow-sm);
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 20px;
    background-color: var(--color-medium-gray);
  }
`;

const ProcessNumber = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--color-dark-gray);
  color: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin: 0 auto var(--spacing-md);
  font-family: var(--font-heading);
`;

const ProcessTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
`;

const ProcessDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
`;

// CTA Section
const CtaContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CtaText = styled.p`
  margin-bottom: var(--spacing-lg);
  font-size: 1.1rem;
`;

// Process Images
const ProcessImagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  justify-self: end; /* placer la colonne images à droite */
`;

const ProcessImage = styled.img`
  width: 100%;
  height: 380px;
  object-fit: contain;
  background-color: transparent;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow-sm);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--box-shadow-md);
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ServicesPage: React.FC = () => {
  return (
    <>
      {/* Espacement pour éviter chevauchement avec le header fixe */}
      <div style={{ paddingTop: '150px' }}>
        <SectionContainer background="white" spacing="small">
        <PageHeader 
          title="Notre Processus" 
          subtitle="Une méthodologie éprouvée depuis plus de 20 ans pour transformer votre souhait en réalité" 
        />
        
        <ProcessContainer>
          {/* Rangée 1: Étape 1 + Image 1 */}
          <ProcessRow>
            <ProcessGrid>
              <ProcessStep>
                <ProcessNumber>1</ProcessNumber>
                <ProcessTitle>Consultation</ProcessTitle>
                <ProcessDescription>
                  Rencontre initiale pour comprendre vos besoins, votre style et vos objectifs.
                  Discussions régulières pour vous accompagner au mieux dans la prise de décision.
                </ProcessDescription>
              </ProcessStep>
            </ProcessGrid>
            <ProcessImagesContainer>
              <ProcessImage 
                src="/processus/consultation.png" 
                alt="Consultation - Étape 1 du processus"
              />
            </ProcessImagesContainer>
          </ProcessRow>

          {/* Rangée 2: Étape 2 + Image 2 */}
          <ProcessRow>
            <ProcessGrid>
              <ProcessStep>
                <ProcessNumber>2</ProcessNumber>
                <ProcessTitle>Conception</ProcessTitle>
                <ProcessDescription>
                  Elaboration des planc, sélection des matériaux et création des rendus visuels, Plans 2D - 3D - élevations - Moodboard...
                </ProcessDescription>
              </ProcessStep>
            </ProcessGrid>
            <ProcessImagesContainer>
              <ProcessImage 
                src="/processus/conception.png" 
                alt="Conception - Étape 2 du processus"
              />
            </ProcessImagesContainer>
          </ProcessRow>

          {/* Rangée 3: Étape 3 + Image 3 */}
          <ProcessRow>
            <ProcessGrid>
              <ProcessStep>
                <ProcessNumber>3</ProcessNumber>
                <ProcessTitle>Réalisation</ProcessTitle>
                <ProcessDescription>
                  Mise en place des équipes et supervisation des travaux...
                </ProcessDescription>
              </ProcessStep>
            </ProcessGrid>
            <ProcessImagesContainer>
              <ProcessImage 
                src="/processus/réalisation.png" 
                alt="Réalisation - Étape 3 du processus"
              />
            </ProcessImagesContainer>
          </ProcessRow>

          {/* Rangée 4: Étape 4 + Image 4 */}
          <ProcessRow>
            <ProcessGrid>
              <ProcessStep>
                <ProcessNumber>4</ProcessNumber>
                <ProcessTitle>Finalisation</ProcessTitle>
                <ProcessDescription>
                  Installation des éléments décoratifs jusqu'à la remise des clés de votre nouvel espace.
                </ProcessDescription>
              </ProcessStep>
            </ProcessGrid>
            <ProcessImagesContainer>
              <ProcessImage 
                src="/processus/Finalisation.jpeg" 
                alt="Finalisation - Étape 4 du processus"
              />
            </ProcessImagesContainer>
          </ProcessRow>
        </ProcessContainer>
      </SectionContainer>
      
      <SectionContainer background="light-gray">
        <CtaContent>
          <CtaText>
           Discutons de votre projet de rénovation ou de décoration d'intérieur.
          </CtaText>
          <Button to="/contact" size="large">Prendre rendez-vous</Button>
        </CtaContent>
      </SectionContainer>
      </div>
    </>
  );
};

export default ServicesPage;
